import React, { useEffect, useRef, useState } from 'react';
import { FileIcon, Image as ImageIcon, Upload, X, Copy } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';

import axios from 'axios';

export default function HomePage() {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImage, setIsImage] = useState(false);
  const [extractedText, setExtractedText] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [imageType, setImageType] = useState('leftHalfImage');
  const [notification, setNotification] = useState('');
  const fileInputRef = useRef(null);

  const API_URL = process.env.REACT_APP_API_URL;
  const API_KEY = process.env.REACT_APP_API_KEY;

  const showNotification = message => {
    setNotification(message);
    setTimeout(() => {
      setNotification('');
    }, 2000);
  };

  const processSelectedFile = selectedFile => {
    if (!selectedFile) return;
    setFile(selectedFile);
    setExtractedText('');
    setImageType('leftHalfImage');
  };

  const handleFileChange = e => {
    if (e.target.files && e.target.files[0]) {
      processSelectedFile(e.target.files[0]);
    }
  };

  // Generate preview URL when file changes
  useEffect(() => {
    if (file) {
      // Check if file is an image
      const fileType = file.type.split('/')[0];
      setIsImage(fileType === 'image');

      if (fileType === 'image') {
        const objectUrl = URL.createObjectURL(file);
        setPreview(objectUrl);

        // Free memory when component unmounts
        return () => URL.revokeObjectURL(objectUrl);
      } else {
        setPreview(null);
      }
    }
  }, [file]);

  useEffect(() => {
    const handlePaste = event => {
      const clipboardItems = event.clipboardData?.items;
      if (!clipboardItems) return;

      for (const item of clipboardItems) {
        if (item.type.startsWith('image/')) {
          const pastedFile = item.getAsFile();
          if (pastedFile) {
            processSelectedFile(pastedFile);
            showNotification('Image pasted from clipboard');
          }
          return;
        }
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const handleDragEnter = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = e => {
    e.preventDefault();
    e.stopPropagation();
    if (!isDragging) {
      setIsDragging(true);
    }
  };

  const handleDrop = e => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      processSelectedFile(e.dataTransfer.files[0]);
    }
  };

  const handleRemoveFile = e => {
    e.stopPropagation();
    setFile(null);
    setPreview(null);
    setIsImage(false);
    setImageType('leftHalfImage');
    setExtractedText('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  async function cropHalf(file, type) {
    if (!(file instanceof Blob)) {
      throw new Error('Invalid file');
    }

    return new Promise((resolve, reject) => {
      const img = new Image();
      const reader = new FileReader();

      reader.onload = event => {
        img.src = event.target.result;
      };

      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        // Set canvas size to half of the image width
        canvas.width = Math.floor(img.width / 2);
        canvas.height = img.height;

        if (type === 'left') {
          // Draw only the left half of the image
          ctx.drawImage(img, 0, 0, img.width / 2, img.height, 0, 0, canvas.width, canvas.height);
        } else {
          ctx.drawImage(
            img,
            img.width / 2,
            0,
            img.width / 2,
            img.height,
            0,
            0,
            canvas.width,
            canvas.height
          );
        }
        // Determine the output format based on the input file
        const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
        const outputQuality = outputType === 'image/jpeg' ? 0.9 : undefined;

        // Convert canvas to Blob and return as File with original format
        canvas.toBlob(
          blob => {
            if (blob) {
              // Preserve original extension
              let fileName;
              if (file.name) {
                fileName = `cropped_${file.name}`;
              } else {
                // Default filename with appropriate extension
                const extension = outputType === 'image/png' ? '.png' : '.jpg';
                fileName = `cropped_image${extension}`;
              }

              resolve(new File([blob], fileName, { type: outputType }));
            } else {
              reject(new Error('Error creating cropped image'));
            }
          },
          outputType,
          outputQuality
        );
      };

      img.onerror = () => reject(new Error('Error loading image'));
      reader.onerror = () => reject(new Error('Error reading file'));

      // Read file as Data URL
      reader.readAsDataURL(file);
    });
  }

  async function handleExtractText() {
    if (!file || !(file instanceof Blob)) {
      console.error('Invalid file selected');
      return;
    }

    // Check if file is PNG or JPEG
    if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
      console.error('Unsupported file type. Please upload PNG or JPEG images only.');
      return;
    }

    setIsLoading(true);
    try {
      let croppedFile;
      if (imageType === 'fullImage') {
        croppedFile = file;
      } else if (imageType === 'rightHalfImage') {
        croppedFile = await cropHalf(file, 'right');
      } else {
        croppedFile = await cropHalf(file, 'left');
      }
      // Prepare FormData with the cropped image
      const formData = new FormData();
      formData.append('file', croppedFile);
      formData.append('apikey', API_KEY);
      formData.append('language', 'eng');
      formData.append('isOverlayRequired', 'false');

      // Send request to OCR API
      let response;
      await axios
        .post(API_URL, formData)
        .then(res => {
          response = res;
          console.log(res.data);
        })
        .catch(err => {
          console.log(err);
        });

      // Check if ParsedResults exists and has at least one item
      if (response.data && response.data.ParsedResults && response.data.ParsedResults.length > 0) {
        const text = response.data.ParsedResults[0].ParsedText || 'No text extracted';

        setExtractedText(text);
        return text;
      } else {
        console.log('No text extracted');
        setExtractedText('No text extracted');
        return '';
      }
    } catch (error) {
      console.error('Error extracting text:', error);
      setExtractedText('Error extracting text. Please try again.');
    } finally {
      setIsLoading(false);
    }
  }

  function handleCopyText() {
    navigator.clipboard.writeText(extractedText);
    showNotification('Text copied');
  }

  return (
    <div className="w-full min-h-screen overflow-x-hidden bg-[var(--color-bg)] transition-colors duration-300">
      <h1 className="font-semibold text-center pt-6 text-2xl sm:text-3xl text-[var(--color-primary)] px-4">
        Image To Text Converter
      </h1>
      <p className="text-center text-sm sm:text-base text-[var(--color-text-secondary)] mt-2 px-4">
        Upload, drag and drop, or press <span className="font-semibold">Ctrl+V / Cmd+V</span> to
        paste an image instantly.
      </p>

      {notification && (
        <AnimatePresence>
          <motion.div
            className="fixed top-4 left-1/2 -translate-x-1/2 px-4 py-2 text-[var(--color-text)] border border-[var(--color-border)] flex items-center rounded-lg z-50 bg-[var(--color-surface)]"
            initial={{ y: -12, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.2 }}
            style={{ boxShadow: 'var(--shadow-sm)' }}
          >
            <p className="text-sm">{notification}</p>
          </motion.div>
        </AnimatePresence>
      )}

      <div className="w-full px-4 sm:px-6 max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg mx-auto mt-6 sm:mt-8">
        {!file ? (
          <div
            className={`border-2 border-dashed rounded-xl p-5 sm:p-7 transition-colors duration-200
                        ${
                          isDragging
                            ? 'border-[var(--color-drop-zone-border)] bg-[var(--color-drop-zone)]'
                            : 'border-[var(--color-border)] hover:border-[var(--color-primary)]'
                        }
                        bg-[var(--color-surface)]`}
            style={{ boxShadow: 'var(--shadow-card)' }}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current.click()}
          >
            <input
              type="file"
              className="hidden"
              onChange={handleFileChange}
              ref={fileInputRef}
              accept="image/*"
            />
            <div className="flex flex-col items-center justify-center text-center">
              <Upload className="w-9 h-9 sm:w-11 sm:h-11 mb-2 sm:mb-3 text-[var(--color-text-secondary)]" />
              <p className="mb-1 sm:mb-2 text-xs sm:text-sm font-medium text-[var(--color-text)]">
                <span className="font-bold">Click to upload</span>, drag and drop, or paste
              </p>
              <p className="text-xs text-[var(--color-text-secondary)]">
                JPG, PNG • Ctrl+V / Cmd+V
              </p>
            </div>
          </div>
        ) : (
          <div
            className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] overflow-hidden transition-colors duration-300"
            style={{ boxShadow: 'var(--shadow-card)' }}
          >
            {isImage && preview && (
              <div className="relative w-full aspect-video bg-[var(--color-border-light)] overflow-hidden">
                <img src={preview} alt="Preview" className="w-full h-full object-contain" />
              </div>
            )}

            <div className="p-3 sm:p-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 sm:gap-3">
                <div className="flex items-center space-x-2 sm:space-x-3">
                  <div className="p-1 sm:p-2 bg-[var(--color-surface-active)] rounded-md">
                    {isImage ? (
                      <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--color-text)]" />
                    ) : (
                      <FileIcon className="w-5 h-5 sm:w-6 sm:h-6 text-[var(--color-text)]" />
                    )}
                  </div>
                  <div className="text-xs sm:text-sm font-medium truncate max-w-[160px] sm:max-w-xs text-[var(--color-text)]">
                    {file.name}
                  </div>
                </div>

                <button
                  onClick={handleRemoveFile}
                  className="flex items-center justify-center px-2 sm:px-3 py-1 sm:py-2 text-xs sm:text-sm font-medium rounded-md text-[var(--color-text)] bg-[var(--color-surface-active)] hover:bg-[var(--color-surface-hover)] transition-colors duration-200"
                >
                  <X className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {file && !extractedText && (
        <div className="flex flex-col sm:flex-row justify-center items-center mt-4 gap-2 sm:gap-4 px-4">
          <button
            className={`w-full sm:w-auto px-3 py-2 rounded-xl font-semibold text-sm transition-all duration-300 ${
              isLoading
                ? 'bg-[var(--color-surface-active)] text-[var(--color-text-muted)] cursor-not-allowed'
                : 'bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]'
            }`}
            onClick={handleExtractText}
            disabled={isLoading}
          >
            {isLoading ? 'Processing...' : 'Extract Text'}
          </button>

          {!isLoading && (
            <select
              className="w-full sm:w-auto px-3 py-2 rounded-xl font-semibold text-sm transition-all duration-300 
                                      bg-[var(--color-surface)] border border-[var(--color-border)] text-[var(--color-text)]
                                      appearance-none cursor-pointer"
              onChange={e => setImageType(e.target.value)}
            >
              <option value="leftHalfImage">Left Half</option>
              <option value="fullImage">Full Image</option>

              <option value="rightHalfImage">Right Half</option>
            </select>
          )}
        </div>
      )}

      {extractedText && (
        <div className="flex justify-center items-center flex-col px-4">
          <div className="w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-[70vw] mx-auto mt-4 sm:mt-6">
            <div
              className="bg-[var(--color-surface)] rounded-xl border border-[var(--color-border)] p-3 sm:p-4 transition-colors duration-300 w-full"
              style={{ boxShadow: 'var(--shadow-card)' }}
            >
              <div className="flex flex-row items-center justify-between">
                <h3 className="font-medium text-base sm:text-lg mb-2 text-[var(--color-text)]">
                  Extracted Text:
                </h3>
                <button
                  onClick={handleCopyText}
                  className="p-1 rounded-md hover:bg-[var(--color-surface-hover)] transition-colors"
                  aria-label="Copy text"
                >
                  <Copy className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--color-text)]" />
                </button>
              </div>

              <div className="bg-[var(--color-surface-active)] p-2 sm:p-3 rounded-md text-[var(--color-text-secondary)] whitespace-pre-line text-xs sm:text-sm overflow-auto max-h-60 sm:max-h-80">
                {extractedText || 'No text extracted'}
              </div>
            </div>
          </div>

          <button
            className="my-4 sm:my-5 px-3 py-2 rounded-xl font-semibold text-sm transition-all duration-300
                                bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary-hover)]"
            onClick={handleRemoveFile}
          >
            Convert New
          </button>
        </div>
      )}
      <p className="text-center text-xs sm:text-sm text-[var(--color-text-muted)] mt-3 pb-5">
        Built by{' '}
        <a
          href="https://github.com/ashwinn-si"
          target="_blank"
          rel="noopener noreferrer"
          className="text-[var(--color-primary)] underline-offset-2 hover:text-[var(--color-text)] transition-colors"
        >
          Ashwin S I
        </a>
      </p>
    </div>
  );
}
